import { Location } from '@angular/common';
import { Component, HostListener, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork, UtilityService } from 'app/core/core.module';
import { FileService } from 'app/core/file.service';
import { Product, ProductCategory, ProductMaterial, ProductUnitOfMeasure, Quote, QuoteLine, QuoteStatus } from 'app/core/model/entity-model';
import { ReportService } from 'app/core/report.service';
import { Utility } from 'app/core/utility';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { InputDialogComponent, SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { QuoteLineDialogComponent } from '../quote-line-dialog/quote-line-dialog.component';
import moment from 'moment';
import { Subject } from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-quote-detail',
    templateUrl: './quote-detail.component.html',
    styleUrl: './quote-detail.component.scss',
})
export class QuoteDetailComponent {
    uow = inject(BrewersUnitOfWork);
    route = inject(ActivatedRoute);
    router = inject(Router);
    auth = inject(AuthService);
    modalService = inject(BsModalService);
    appInfo = inject(AppInformationService);
    location = inject(Location);
    fileService = inject(FileService);
    reportService = inject(ReportService);
    utility = inject(UtilityService);

    result = new Subject<{ success: boolean; value?: Quote }>();
    quoteId: string | number;
    quote: Quote;
    quoteLines: QuoteLine[];
    statuses: QuoteStatus[];
    customers: { id: number; name: string }[];
    customersDS: DataSource;
    productCategories: ProductCategory[];
    productMaterials: ProductMaterial[];
    readOnly = true;
    processing = false;
    taxableSubTotal: number = 0;
    showAsDialog = false;

    QuoteStatuses = QuoteStatus.Statuses;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    async ngOnInit() {
        this.statuses = await this.uow.quoteStatuses.whereActive();
        await this.getCustomers();

        if (!this.quoteId) this.quoteId = this.route.snapshot.params['id'];
        await this.getData();
    }

    async getData() {
        this.productCategories = await this.uow.productCategories.whereActive('displayOrder, name');
        this.productMaterials = await this.uow.productMaterials.whereActive();
        await this.getQuote();
        this.setTitle();
        this.setPermissions();
        this.calcTotals();
    }

    async getQuote() {
        if (this.showAsDialog) return;

        if (this.quoteId === 'new') {
            const today = new Date();
            this.quote = this.uow.quotes.createEntity({
                statusId: QuoteStatus.Statuses.New,
                created: today,
                createdBy: this.auth.currentUser.userName,
                quoteCode: crypto.randomUUID(),
                quoteDate: today,
                expirationDate: moment(today).add(30, 'days').toDate(),
                quoteNumber: null,
            });
            this.quoteLines = [];
        } else {
            this.quote = await this.uow.quotes.byId(this.quoteId, 'customer, quoteLines.product');
            this.quoteLines = this.quote.quoteLines.filter((l) => !l.deleted).slice();
        }
    }

    async getCustomers() {
        this.customers = await this.uow.customers.getLookupCustomers();
        this.customersDS = new DataSource({ store: this.customers, paginate: true });
    }

    hasChanges() {
        return this.uow.hasChanges();
    }
    setTitle() {
        if (this.showAsDialog) return;
        this.appInfo.title = this.quoteId === 'new' ? 'Quote Detail - New' : `${this.quote.customer?.companyName}# ${this.quote.quoteNumber}`;
    }
    setPermissions() {
        this.readOnly = this.quote.statusId === QuoteStatus.Statuses.Awarded || this.quote.statusId === QuoteStatus.Statuses.Declined;
    }

    async onRowDoubleClick(e) {
        if (this.readOnly) return;
        this.openLine(e.data);
    }

    async delete() {
        const result = await confirm(`Are you sure you want to delete this quote?`, this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const date = new Date();
        this.quote.deleted = date;
        this.quote.deletedBy = this.auth.currentUser.userName;
        this.quote.quoteLines.forEach((line) => {
            line.deleted = date;
            line.deletedBy = this.auth.currentUser.userName;
        });
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();

        this.router.navigate(['/customers/quotes/overview']);
    }

    customerSelectionChanged = async (e: any) => {
        const customerId = e.value;
        this.appInfo.pleaseWaitShow();
        this.quote.customer = customerId ? await this.uow.customers.byId(this.quote.customerId) : null;
        this.copyBilling();
        this.calcTotals();
        this.appInfo.pleaseWaitHide();
    };

    freightChange = (e) => {
        this.calcTotals();
    };

    copyBilling() {
        const customer = this.quote.customer;
        this.quote.billToContactName = customer?.contactName;
        this.quote.billToAddress1 = customer?.billAddress1;
        this.quote.billToAddress2 = customer?.billAddress2;
        this.quote.billToCity = customer?.billCity;
        this.quote.billToState = customer?.billState;
        this.quote.billToZip = customer?.billZip;
        this.quote.billToCountry = customer?.billCountry;
        this.quote.billToPhone = customer?.phone;
        this.quote.billToEmail = customer?.email;
        if (!this.quote.notes || this.quote.notes.trim() === '') this.quote.notes = customer?.notes;
    }

    async changeStatus(status: string) {
        switch (status) {
            case 'pending':
                this.quote.statusId = 2;
                break;
            case 'awarded':
                this.quote.statusId = 3;
                break;
            case 'declined':
                this.quote.statusId = 4;
                break;
        }
        await this.save();
        await this.getData();
    }

    calcTotals() {
        this.calcSubTotal();
        this.calcTax();

        if (this.quote.total !== Utility.round(this.quote.subTotal, 2) + Utility.round(this.quote.tax, 2))
            this.quote.total = Utility.round(this.quote.subTotal + this.quote.tax, 2);
    }

    calcSubTotal() {
        let subTotal = 0;
        let taxableSubTotal = 0;
        this.quoteLines
            .filter((x) => !x.deleted)
            .forEach((line) => {
                subTotal += Utility.round(line.quantity * line.price, 2);
                if (line.isTaxable) taxableSubTotal += Utility.round(line.quantity * line.price, 2);
            });
        if (this.quote.subTotal !== Utility.round(subTotal, 2)) this.quote.subTotal = Utility.round(subTotal, 2);
        this.taxableSubTotal = Utility.round(taxableSubTotal, 2);
    }

    calcTax() {
        let tax = 0;
        tax = Utility.round(Utility.SalesTaxRate * this.taxableSubTotal, 2);
        if (this.quote.tax !== Utility.round(tax, 2)) this.quote.tax = Utility.round(tax, 2);
    }

    async addProductDelivery(quoteLines: QuoteLine[]) {
        if (!quoteLines || quoteLines.length === 0) return;

        let error = '';
        if (quoteLines.some((quoteLine) => quoteLine.isMisc)) error = 'Cannot create product deliveries for misc lines';
        else if (!quoteLines[0].productId || !quoteLines.every((quoteLine) => quoteLine.productId === quoteLines[0].productId)) error = 'All lines must have the same product';
        if (error) {
            await alert(error, this.appInfo.settings.company);
            return;
        }

        const quantity = _(quoteLines)
            .sumBy((quoteLine) => quoteLine.quantity)
            .valueOf();

        const product = quoteLines[0].product;
        if (product.deliveryProductId) await this.addLineFromDeliveryProduct(product.deliveryProductId, quantity);
        else await this.openLine(null, quantity, 'delivery');
    }

    async addLineFromDeliveryProduct(deliveryProductId: number, quantity: number) {
        const deliveryProduct = await this.uow.products.byId(deliveryProductId, 'inventoryUom');
        const line = this.uow.quoteLines.createEntity({
            productId: deliveryProductId,
            partNumber: deliveryProduct.partNumber,
            unit: deliveryProduct.inventoryUom?.name,
            description: deliveryProduct.description,
            price: deliveryProduct.basePrice,
            isTaxable: deliveryProduct.category.isTaxable,
            quantity: quantity,
            quoteId: this.quote.id,
            lineNumber: this.nextLineNumber(),
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        });
        this.quoteLines.push(line);
        await this.save();
        await this.linesGrid.instance.refresh();
    }

    async openLine(quoteLine?: QuoteLine, quantity?: number, mode?: 'delivery') {
        if (quoteLine && quoteLine.product?.material.name === 'DELIVERY') mode = 'delivery';
        const success = await this.save();
        if (!success) return;
        const modalRef = this.modalService.show(QuoteLineDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                quoteId: this.quote.id,
                lineNumber: this.nextLineNumber(),
                quoteLine: quoteLine,
                quantity: quantity,
                mode: mode,
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                if (!this.quoteLines.includes(result.value)) this.quoteLines.push(result.value);
                await this.save();
                await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    async addDelivery() {
        const success = await this.save();
        if (!success) return;
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Delivery',
                dialogPrompt: 'Price',
                dialogValue: 100,
                mode: 'text',
                checkNumeric: true,
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success && result.value) {
                const line = this.uow.quoteLines.createEntity({
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    description: 'Delivery',
                    quoteId: this.quote.id,
                    isMisc: true,
                    lineNumber: this.nextLineNumber(),
                    partNumber: 'Delivery',
                    price: result.value,
                    productId: Product.Types.Delivery,
                    quantity: 1,
                    unit: 'EA',
                    unitOfMeasureId: ProductUnitOfMeasure.Types.Each,
                });
                this.quoteLines.push(line);
                await this.save();
                await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    async addMisc() {
        const success = await this.save();
        if (!success) return;
        this.appInfo.pleaseWaitShow();
        const line = this.uow.quoteLines.createEntity({
            quantity: 1,
            price: 0,
            quoteId: this.quoteId,
            lineNumber: this.nextLineNumber(),
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
            isMisc: true,
        });
        this.quoteLines.push(line);
        await this.save();
        await this.linesGrid.instance.refresh();
        this.appInfo.pleaseWaitHide();
    }

    async removeLine(quoteLines?: QuoteLine[]) {
        const save = await this.save();
        if (!save) return;

        const result = await confirm(`Are you sure you want to remove the selected quote line?`, this.appInfo.appTitle);
        if (!result) return;

        const date = new Date();
        quoteLines.forEach((line) => {
            line.deleted = date;
            line.deletedBy = this.auth.currentUser.userName;
        });
        this.quoteLines = this.quoteLines.filter((line) => !quoteLines.includes(line));
        this.updateDisplayOrder();
        await this.save();

        await this.getData();
        this.calcTotals();
        await this.save();
    }

    toggleIsTaxable(lines: QuoteLine[]) {
        if (this.readOnly || !lines || lines.length === 0) return;
        const isTaxable = lines.every((line) => line.isTaxable);
        lines.forEach((line) => (line.isTaxable = !isTaxable));
        this.calcTotals();
    }

    async save() {
        const success = await Promise.resolve(this.validate());
        if (!success) return false;
        this.appInfo.pleaseWaitShow();
        this.calcTotals();

        if (!this.quote.quoteNumber) this.quote.quoteNumber = await this.utility.getNextNumber('QuoteNumber');

        await this.uow.commit();
        if (!this.showAsDialog && this.quoteId === 'new') {
            this.quoteId = this.quote.id;
            this.router.navigate(['/customers/quotes/' + this.quote.id], { replaceUrl: true });
            this.setTitle();
        }
        this.appInfo.pleaseWaitHide();
        return true;
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    async dialogSave() {
        this.quote.statusId = QuoteStatus.Statuses.Awarded;
        const success = await this.save();
        if (!success) return;
        this.result.next({ success: true, value: this.quote });
    }

    dialogCancel() {
        this.uow.rollback();
        this.result.next({ success: false });
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig('mainValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('mainValidationGroup');
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before saving.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    async email() {
        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'Quote',
                entity: this.quote,
                to: this.quote.customer?.email,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            modalRef.hide();
        });
    }

    async print() {
        await this.reportService.printQuoteReport(this.quote);
    }

    async move(quoteLine: QuoteLine, direction: 'up' | 'down') {
        if (this.processing) return;
        this.processing = true;
        const increment = direction === 'down' ? 1 : -1;
        const otherQuoteLine = this.quoteLines.find((otherQuoteLine) => otherQuoteLine.lineNumber === quoteLine.lineNumber + increment);
        if (otherQuoteLine) {
            quoteLine.lineNumber += increment;
            otherQuoteLine.lineNumber -= increment;
            this.sort(this.quoteLines);
            await this.save();
        }
        this.processing = false;
    }

    sort(quoteLines: QuoteLine[]) {
        quoteLines.sort((a, b) => (a.lineNumber || 9999) - (b.lineNumber || 9999));
    }

    updateDisplayOrder() {
        _.orderBy(this.quoteLines, 'lineNumber').forEach((quoteLine, index) => {
            if (quoteLine.lineNumber !== index + 1) quoteLine.lineNumber = index + 1;
        });
    }

    nextLineNumber() {
        return (_(this.quoteLines).maxBy((l) => l.lineNumber)?.lineNumber ?? 0) + 1;
    }
}
